<script lang="ts">
  // The Files view's commit range picker over a track (logic/track.ts):
  // its points (commits replaced by force-pushes on a dimmed lane above),
  // two handles, A (from) and B (to), that go on any two points in either
  // order, and presets. The URL's `from`/`to` are the source of truth.
  // Where the track came from is the page's business, not this one's.
  import { controlCommand } from '../keys/control';
  import { timelineHandleKeys, timelineMenuKeys, timelinePointKeys } from '../keys/controls';
  import { onDestroy, tick } from 'svelte';
  import { nav } from '../nav.svelte';
  import { prefs } from '../state/prefs.svelte';
  import { relTime } from '../logic/time';
  import type { Track } from '../logic/track';
  import type { Facets } from '../subject';
  import {
    comparisonQuery,
    keyMove,
    pointTitle,
    presets,
    rangeFromQuery,
    rangeSummary,
    rangeToQuery,
    reversed,
    setHandle,
    spanOf,
    timelineOf,
    type Handle,
    type Range,
    type RangePoint,
  } from '../logic/range';

  interface Props {
    track: Track;
    /** The subject's facets: which presets exist, and where a range opens. */
    facets: Facets;
    /**
     * Reading older commits than the track holds (a branch history, read a
     * window at a time): the "older" lead-in then reads one. Null where the
     * page reads none.
     */
    older?: { can: boolean; loading: boolean; load: () => Promise<void> } | null;
  }
  let { track, facets, older = null }: Props = $props();

  const HANDLES: Handle[] = ['from', 'to'];
  const LETTER: Record<Handle, string> = { from: 'A', to: 'B' };
  const NAME: Record<Handle, string> = { from: 'From', to: 'To' };
  /** Column width and side padding of the track (px). */
  const COL = 56;
  const PAD = 30;
  /** Keyboard moves settle into the URL (and a new diff) after this pause. */
  const SETTLE_MS = 450;

  const timeline = $derived(timelineOf(track, facets));
  const points = $derived(timeline.points);
  const url = $derived(rangeFromQuery(timeline, nav.query));
  /** A range being dragged or keyed, not yet in the URL. */
  let pending = $state<Range | null>(null);
  const shown = $derived(pending ?? url.range);
  const choices = $derived(presets(timeline));
  const collapsed = $derived(prefs.value.timelineCollapsed);
  const hasReplaced = $derived(points.some((p) => p.lane === 'replaced'));
  const newCount = $derived(points.filter((p) => p.isNew && p.lane === 'main').length);
  const summary = $derived(
    rangeSummary(points, shown, pending ? (points[shown.from]?.value ?? url.from) : url.from, pending ? (points[shown.to]?.value ?? url.to) : url.to),
  );
  const span = $derived(spanOf(points, shown));
  const backwards = $derived(reversed(shown));

  // Lane centers (px from the track's top).
  const yReplaced = 16;
  const yMain = $derived(hasReplaced ? 56 : 18);
  const yHandle = $derived(yMain + 38);
  const height = $derived(yHandle + 14);
  const width = $derived(PAD * 2 + Math.max(0, points.length - 1) * COL);
  const x = (i: number) => PAD + i * COL;
  const y = (p: RangePoint) => (p.lane === 'replaced' ? yReplaced : yMain);

  // ---------------------------------------------------------------- committing

  let settle: ReturnType<typeof setTimeout> | null = null;
  onDestroy(() => {
    if (settle) clearTimeout(settle);
  });

  function commit(r: Range) {
    if (settle) clearTimeout(settle);
    settle = null;
    pending = null;
    if (r.from === r.to || r.from < 0 || r.to < 0) return;
    nav.setQuery(rangeToQuery(timeline, r));
  }

  /**
   * Read older commits from the lead-in: a range being keyed or dragged
   * lands first (it is held by point index, which the older points shift),
   * and the track keeps its place from the right, where the reader was,
   * as the older points come in on the left.
   */
  async function loadOlder() {
    if (!older?.can || older.loading) return;
    if (pending) commit(pending);
    const right = scroller ? scroller.scrollWidth - scroller.scrollLeft : null;
    await older.load();
    await tick();
    if (scroller && right !== null) scroller.scrollLeft = scroller.scrollWidth - right;
  }

  function commitLater(r: Range) {
    pending = r;
    if (settle) clearTimeout(settle);
    settle = setTimeout(() => commit(r), SETTLE_MS);
  }

  const sameRange = (a: Range | null, b: Range) => !!a && a.from === b.from && a.to === b.to;

  /** A and B trade places (by URL value, so an end off the timeline swaps too). */
  function swapEnds() {
    if (settle) clearTimeout(settle);
    settle = null;
    pending = null;
    nav.setQuery(comparisonQuery(timeline, url.to, url.from));
  }

  // ---------------------------------------------------------------- points

  /** The point holding the points' single tab stop (roving focus). */
  let focusIdx = $state(-1);
  const rovingIdx = $derived(focusIdx >= 0 && focusIdx < points.length ? focusIdx : shown.to >= 0 ? shown.to : points.length - 1);
  let trackEl: HTMLDivElement | undefined = $state();
  let scroller: HTMLDivElement | undefined = $state();
  let bodyEl: HTMLDivElement | undefined = $state();

  function pointEl(i: number): HTMLButtonElement | null {
    return trackEl?.querySelector<HTMLButtonElement>(`.rtl-point[data-i="${i}"]`) ?? null;
  }

  /** A commit point's keys (see `timelinePointKeys`). */
  function onPointKey(e: KeyboardEvent, i: number) {
    const c = controlCommand(timelinePointKeys, e);
    if (!c) return;
    e.preventDefault();
    e.stopPropagation();
    if (c.kind === 'set') return void commit(setHandle(shown, c.handle, i));
    if (c.kind === 'menu') return void openMenu(i);
    const to = c.to === 'prev' ? Math.max(0, i - 1) : c.to === 'next' ? Math.min(points.length - 1, i + 1) : c.to === 'first' ? 0 : points.length - 1;
    focusIdx = to;
    pointEl(to)?.focus();
  }

  function onPointClick(e: MouseEvent, i: number) {
    // Keyboard activation is handled on keydown.
    if (e.detail === 0) return;
    focusIdx = i;
    if (e.shiftKey) commit(setHandle(shown, 'to', i));
    else if (e.altKey) commit(setHandle(shown, 'from', i));
    else if (menu?.i === i) closeMenu(true);
    else openMenu(i);
  }

  // ---------------------------------------------------------------- the point menu

  /** The open "Set as From / Set as To" menu: its point and position in the body. */
  let menu = $state<{ i: number; left: number; top: number; width: number } | null>(null);
  let menuEl: HTMLDivElement | undefined = $state();

  async function openMenu(i: number) {
    const el = pointEl(i);
    if (!el || !bodyEl) return;
    tip = null;
    const pt = el.getBoundingClientRect();
    const br = bodyEl.getBoundingClientRect();
    const width = Math.min(280, br.width);
    const left = Math.max(0, Math.min(br.width - width, pt.left + pt.width / 2 - br.left - width / 2));
    // Below the track, so it covers no point.
    const below = scroller ? scroller.getBoundingClientRect().bottom : pt.bottom;
    menu = { i, left, top: below - br.top + 2, width };
    await tick();
    menuEl?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
  }

  /** Close the menu; `refocus` returns focus to its point. */
  function closeMenu(refocus: boolean) {
    const i = menu?.i;
    menu = null;
    if (refocus && i !== undefined) pointEl(i)?.focus();
  }

  function choose(h: Handle) {
    if (!menu) return;
    const i = menu.i;
    commit(setHandle(shown, h, i));
    closeMenu(true);
  }

  /** The menu's keys (see `timelineMenuKeys`); letters stay with the menu, not the diff reader's shortcuts. */
  function onMenuKey(e: KeyboardEvent) {
    const c = controlCommand(timelineMenuKeys, e);
    if (c === 'close') {
      e.preventDefault();
      e.stopPropagation();
      closeMenu(true);
      return;
    }
    if (c) {
      const buttons = [...(menuEl?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
      const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (!buttons.length) return;
      e.preventDefault();
      e.stopPropagation();
      buttons[(at + (c === 'prev' ? -1 : 1) + buttons.length) % buttons.length].focus();
      return;
    }
    if (e.key.length === 1 && !e.metaKey && !e.ctrlKey) e.stopPropagation();
  }

  function onMenuFocusOut(e: FocusEvent) {
    const to = e.relatedTarget as Node | null;
    if (to && (menuEl?.contains(to) || (menu && pointEl(menu.i) === to))) return;
    // Focus left for elsewhere (Tab, a click): close without pulling it back.
    if (to) closeMenu(false);
  }

  function onWindowPointerDown(e: PointerEvent) {
    if (!menu) return;
    const t = e.target as Node;
    if (menuEl?.contains(t) || pointEl(menu.i)?.contains(t)) return;
    closeMenu(false);
  }

  const menuPoint = $derived(menu ? points[menu.i] : undefined);
  const menuDetails = $derived(details(menuPoint));

  function menuDisabled(h: Handle): string | null {
    if (!menu) return null;
    const self = h === 'from' ? shown.from : shown.to;
    const other = h === 'from' ? shown.to : shown.from;
    if (menu.i === self) return `Already ${NAME[h]} (${LETTER[h]})`;
    if (menu.i === other) return `This is ${NAME[h === 'from' ? 'to' : 'from']} (${LETTER[h === 'from' ? 'to' : 'from']}); use Swap to trade the ends`;
    return null;
  }

  // ---------------------------------------------------------------- handles

  let fromEl: HTMLDivElement | undefined = $state();
  let toEl: HTMLDivElement | undefined = $state();

  /** A handle's keys (see `timelineHandleKeys`). */
  function onHandleKey(e: KeyboardEvent, h: Handle) {
    const c = controlCommand(timelineHandleKeys, e);
    if (!c) return;
    if (c.kind === 'apply') {
      e.preventDefault();
      e.stopPropagation();
      if (pending) commit(pending);
      return;
    }
    const moved = keyMove(points, shown, h, c.key);
    if (!moved) return;
    e.preventDefault();
    e.stopPropagation();
    if (sameRange(moved, shown)) return;
    commitLater(moved);
  }

  let drag: { handle: Handle; from: Range; pointer: number } | null = null;
  let dragging = $state<Handle | null>(null);

  /** The column under the pointer: each column holds one point, on either lane. */
  function indexAt(clientX: number): number {
    const r = trackEl!.getBoundingClientRect();
    return Math.max(0, Math.min(points.length - 1, Math.round((clientX - r.left - PAD) / COL)));
  }

  function onHandleDown(e: PointerEvent, h: Handle) {
    if (e.button !== 0 || shown.from < 0 || shown.to < 0) return;
    e.preventDefault();
    menu = null;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    (e.currentTarget as HTMLElement).focus({ preventScroll: true });
    drag = { handle: h, from: shown, pointer: e.pointerId };
    dragging = h;
  }

  function onHandleMove(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.pointer) return;
    pending = setHandle(drag.from, drag.handle, indexAt(e.clientX));
    autoScroll(e.clientX);
  }

  function onHandleUp(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.pointer) return;
    drag = null;
    dragging = null;
    if (pending) commit(pending);
  }

  function autoScroll(clientX: number) {
    if (!scroller) return;
    const r = scroller.getBoundingClientRect();
    if (clientX < r.left + 24) scroller.scrollLeft -= 12;
    else if (clientX > r.right - 24) scroller.scrollLeft += 12;
  }

  // ---------------------------------------------------------------- tooltip

  let tip = $state<{ i: number; left: number; top: number } | null>(null);

  function showTip(i: number, el: HTMLElement) {
    if (menu) return;
    const r = el.getBoundingClientRect();
    const w = Math.min(320, window.innerWidth - 16);
    tip = { i, left: Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2)), top: r.bottom + 6 };
  }

  function hideTip(i: number) {
    if (tip?.i === i) tip = null;
  }

  // ---------------------------------------------------------------- layout

  // Keep the newer end of the range in view when the track opens or the range changes.
  $effect(() => {
    void collapsed;
    const end = Math.max(shown.from, shown.to);
    if (!scroller || drag) return;
    void tick().then(() => {
      if (!scroller) return;
      const target = x(end >= 0 ? end : points.length - 1);
      if (target < scroller.scrollLeft + PAD || target > scroller.scrollLeft + scroller.clientWidth - PAD)
        scroller.scrollLeft = target - scroller.clientWidth + PAD * 2;
    });
  });

  /** Main-lane segments: [from, to, broken]. */
  const segments = $derived.by(() => {
    const out: { a: number; b: number; broken: boolean }[] = [];
    let prev = -1;
    points.forEach((p, i) => {
      if (p.lane !== 'main') return;
      if (prev >= 0) out.push({ a: prev, b: i, broken: p.breakBefore });
      prev = i;
    });
    return out;
  });

  /** Replaced-lane runs, each forking off its main point (`root` -1: one the track does not hold, off its left edge). */
  const forks = $derived.by(() => {
    const out: { root: number; first: number; last: number }[] = [];
    points.forEach((p, i) => {
      if (p.lane !== 'replaced') return;
      const run = out[out.length - 1];
      if (run && run.last === i - 1 && run.root === p.forksOff) run.last = i;
      else out.push({ root: p.forksOff ?? -1, first: i, last: i });
    });
    return out;
  });

  /** SVG path of a replaced run: the fork curve up from its root, or in from the left edge when its root is not held. */
  function forkPath(f: { root: number; first: number; last: number }): string {
    const lane = `L ${x(f.last)} ${yReplaced}`;
    if (f.root < 0) return `M0 ${yReplaced} ${lane}`;
    return `M${x(f.root)} ${yMain} C ${x(f.root) + COL * 0.5} ${yMain}, ${x(f.first) - COL * 0.5} ${yReplaced}, ${x(f.first)} ${yReplaced} ${lane}`;
  }

  /** SVG path of one span edge: straight along a lane, or the fork curve up to the replaced lane. */
  function edgePath(a: number, b: number): string {
    const pa = points[a];
    const pb = points[b];
    if (pa.lane === pb.lane) return `M${x(a)} ${y(pa)} L${x(b)} ${y(pb)}`;
    return `M${x(a)} ${yMain} C ${x(a) + COL * 0.5} ${yMain}, ${x(b) - COL * 0.5} ${yReplaced}, ${x(b)} ${yReplaced}`;
  }

  function label(p: RangePoint): string {
    return p.value === 'base' ? p.message || 'base' : p.short;
  }

  function aria(p: RangePoint, i: number): string {
    const t = pointTitle(p);
    const role = i === shown.from ? ', A (from)' : i === shown.to ? ', B (to)' : '';
    return (i === timeline.reviewed ? t + ' · your last review' : t) + role;
  }

  function handleText(h: Handle): string {
    const i = h === 'from' ? shown.from : shown.to;
    const p = points[i];
    if (!p) return '';
    const lane = p.lane === 'replaced' ? ' (replaced)' : '';
    return `${LETTER[h]} · ${NAME[h]} ${p.value === 'base' ? 'base' : p.short}${p.head ? ' (head)' : ''}${lane}${p.message && p.value !== 'base' ? ': ' + p.message : ''}`;
  }

  function details(p: RangePoint | undefined): { sha: string; title: string; meta: string } | null {
    if (!p) return null;
    if (p.value === 'base') return { sha: 'base', title: p.message ?? '', meta: `merge base${p.short ? ' · branch tip ' + p.short : ''}` };
    const meta = [p.author, relTime(p.at)].filter(Boolean);
    if (p.lane === 'replaced') meta.push(p.forcePushedBy ? `replaced by force-push by ${p.forcePushedBy}` : 'replaced by force-push');
    else if (p.forcePushedBy) meta.push(`force-pushed by ${p.forcePushedBy}`);
    if (p.head) meta.push('head');
    return { sha: p.short, title: p.message ?? (p.lane === 'replaced' ? 'Replaced by a force-push' : 'Commit'), meta: meta.join(' · ') };
  }

  const fromDetails = $derived(details(points[shown.from]));
  const toDetails = $derived(details(points[shown.to]));
  const tipDetails = $derived(tip ? details(points[tip.i]) : null);
</script>

<svelte:window onpointerdown={onWindowPointerDown} />

<section class="rtl" class:collapsed aria-label="Commit range">
  <div class="rtl-bar">
    <button
      type="button"
      class="rtl-toggle"
      aria-expanded={!collapsed}
      aria-controls="rtl-body"
      title={collapsed ? 'Show the commit timeline' : 'Fold the commit timeline to one line'}
      onclick={() => prefs.set('timelineCollapsed', !collapsed)}
    >
      <span class="rtl-caret" aria-hidden="true">▸</span>
      <span class="rtl-eyebrow">Range</span>
      <span class="rtl-summary mono">{summary}</span>
    </button>
    {#if newCount && collapsed}<span class="rtl-newcount">{newCount} new</span>{/if}
    {#if !collapsed}
      <div class="rtl-presets" role="group" aria-label="Range presets">
        {#each choices as c (c.id)}
          <button
            type="button"
            class="btn small"
            class:active={sameRange(c.range, url.range)}
            aria-pressed={sameRange(c.range, url.range)}
            disabled={!!c.disabled}
            title={c.disabled ?? (c.id === 'all' ? 'Base → head' : c.id === 'latest' ? "The head's parent → head" : c.id === 'last-push' ? 'Previous head → current head' : 'Your last reviewed commit → head')}
            onclick={() => c.range && commit(c.range)}>{c.label}</button
          >
        {/each}
      </div>
    {/if}
  </div>

  {#if !collapsed}
    <div id="rtl-body" class="rtl-body" bind:this={bodyEl}>
      <div
        class="rtl-scroll"
        bind:this={scroller}
        onscroll={() => {
          tip = null;
          if (menu) closeMenu(menuEl?.contains(document.activeElement) ?? false);
        }}
      >
        <div class="rtl-track" bind:this={trackEl} style="width:{width}px;height:{height}px">
          <svg class="rtl-lines" width={width} height={height} aria-hidden="true">
            {#if timeline.hasOlder}
              <line class="rtl-older-line" x1="0" y1={yMain} x2={x(timeline.base)} y2={yMain} />
            {/if}
            {#each forks as f (f.first)}
              <path class="rtl-fork" class:unheld={f.root < 0} d={forkPath(f)} />
            {/each}
            {#each segments as s (s.b)}
              <line class="rtl-seg" class:broken={s.broken} x1={x(s.a)} y1={yMain} x2={x(s.b)} y2={yMain} />
              {#if s.broken}
                <g class="rtl-break" transform="translate({x(s.b) - COL / 2} {yMain})">
                  <rect x="-5" y="-8" width="10" height="16" />
                  <line x1="-6" y1="6" x2="-1" y2="-6" />
                  <line x1="1" y1="6" x2="6" y2="-6" />
                </g>
              {/if}
            {/each}
            {#if span}
              {#each span.edges as [a, b] (a + ':' + b)}
                <path class="rtl-band" class:cross={!span.oneLine} d={edgePath(a, b)} />
              {/each}
            {/if}
            {#each [shown.from, shown.to] as i}
              {#if points[i]?.lane === 'replaced'}
                <line class="rtl-guide" x1={x(i)} y1={yReplaced + 8} x2={x(i)} y2={yHandle - 8} />
              {/if}
            {/each}
          </svg>

          {#each HANDLES as h (h)}
            {@const i = h === 'from' ? shown.from : shown.to}
            {#if i >= 0}
              <div
                bind:this={() => (h === 'from' ? fromEl : toEl), (el) => (h === 'from' ? (fromEl = el) : (toEl = el))}
                class="rtl-handle {h}"
                class:dragging={dragging === h}
                role="slider"
                tabindex="0"
                aria-label={h === 'from' ? 'A, from (old side)' : 'B, to (new side)'}
                aria-valuemin={0}
                aria-valuemax={points.length - 1}
                aria-valuenow={i}
                aria-valuetext={handleText(h)}
                aria-orientation="horizontal"
                title="{handleText(h)} · drag, ←/→ along the lane, ↑/↓ to switch lane"
                style="left:{x(i)}px;top:{yHandle}px"
                onkeydown={(e) => onHandleKey(e, h)}
                onpointerdown={(e) => onHandleDown(e, h)}
                onpointermove={onHandleMove}
                onpointerup={onHandleUp}
                onpointercancel={onHandleUp}
                onblur={() => pending && !drag && commit(pending)}
              >
                <span aria-hidden="true">{LETTER[h]}</span>
              </div>
            {/if}
          {/each}

          {#if timeline.hasOlder}
            <!-- Older history: the track holds a window; where the page reads more, the lead-in reads the next. -->
            {#if older?.can}
              <button type="button" class="rtl-older load" disabled={older.loading} title="Load older commits" style="left:2px;top:{yMain}px" onclick={loadOlder}>{older.loading ? 'loading…' : '← older'}</button>
            {:else}
              <span class="rtl-older" role="note" title="Older commits are not shown" style="left:2px;top:{yMain}px">older</span>
            {/if}
          {/if}
          <div class="rtl-points" role="group" aria-label="Commits (Enter: set as From or To; Shift+Enter sets To, Alt+Enter sets From)">
            {#each points as p, i (p.value + p.lane)}
              <button
                type="button"
                class="rtl-point {p.lane}"
                class:base={p.value === 'base'}
                class:head={p.head}
                class:new={p.isNew}
                class:reviewed={i === timeline.reviewed}
                class:inrange={!!span?.points.has(i)}
                class:is-end={i === shown.from || i === shown.to}
                data-i={i}
                tabindex={i === rovingIdx ? 0 : -1}
                aria-label={aria(p, i)}
                aria-haspopup="dialog"
                aria-expanded={menu?.i === i}
                aria-describedby={tip?.i === i ? 'rtl-tip' : undefined}
                style="left:{x(i)}px;top:{y(p)}px"
                onclick={(e) => onPointClick(e, i)}
                onkeydown={(e) => onPointKey(e, i)}
                onfocus={(e) => {
                  focusIdx = i;
                  showTip(i, e.currentTarget);
                }}
                onblur={() => hideTip(i)}
                onpointerenter={(e) => showTip(i, e.currentTarget)}
                onpointerleave={() => hideTip(i)}
              >
                <span class="rtl-dot" aria-hidden="true"></span>
                {#if p.lane === 'main'}<span class="rtl-label" aria-hidden="true">{p.head ? 'head' : label(p)}</span>{/if}
              </button>
            {/each}
          </div>
        </div>
      </div>

      {#if menu && menuDetails}
        <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
        <div
          bind:this={menuEl}
          class="rtl-menu"
          role="dialog"
          tabindex="-1"
          aria-label="Use {menuDetails.sha} as an end of the range"
          style="left:{menu.left}px;top:{menu.top}px;width:{menu.width}px"
          onkeydown={onMenuKey}
          onfocusout={onMenuFocusOut}
        >
          <div class="rtl-menu-head">
            <code>{menuDetails.sha}</code>
            <b>{menuDetails.title}</b>
            {#if menuDetails.meta}<span>{menuDetails.meta}</span>{/if}
          </div>
          <div class="rtl-menu-actions">
            {#each HANDLES as h (h)}
              {@const why = menuDisabled(h)}
              <button type="button" class="btn small" disabled={!!why} title={why ?? `${NAME[h]} (${LETTER[h]}): the ${h === 'from' ? 'old' : 'new'} side of the diff`} onclick={() => choose(h)}
                ><span class="rtl-chip {h}" aria-hidden="true">{LETTER[h]}</span>Set as {NAME[h]}</button
              >
            {/each}
          </div>
          <p class="rtl-menu-hint">Shift-click sets To, Alt-click sets From.</p>
        </div>
      {/if}

      <div class="rtl-ends" aria-live="polite">
        {#if fromDetails}
          <div class="rtl-end">
            <span class="rtl-chip from" aria-hidden="true">A</span><span class="rtl-eyebrow">From</span>
            <code>{fromDetails.sha}</code>
            <span class="rtl-msg" title={fromDetails.title}>{fromDetails.title}</span>
            <span class="rtl-meta">{fromDetails.meta}</span>
          </div>
        {:else}
          <div class="rtl-end"><span class="rtl-chip from" aria-hidden="true">A</span><span class="rtl-eyebrow">From</span><code>{url.from.slice(0, 7)}</code><span class="rtl-meta">not on this timeline</span></div>
        {/if}
        <div class="rtl-mid">
          <span class="rtl-arrow" aria-hidden="true">→</span>
          <button type="button" class="btn small rtl-swap" title="Swap A and B (diff B → A)" onclick={swapEnds}><span aria-hidden="true">⇄</span> Swap</button>
        </div>
        {#if toDetails}
          <div class="rtl-end">
            <span class="rtl-chip to" aria-hidden="true">B</span><span class="rtl-eyebrow">To</span>
            <code>{toDetails.sha}</code>
            <span class="rtl-msg" title={toDetails.title}>{toDetails.title}</span>
            <span class="rtl-meta">{toDetails.meta}</span>
          </div>
        {:else}
          <div class="rtl-end"><span class="rtl-chip to" aria-hidden="true">B</span><span class="rtl-eyebrow">To</span><code>{url.to.slice(0, 7)}</code><span class="rtl-meta">not on this timeline</span></div>
        {/if}
      </div>
      {#if backwards || (span && !span.oneLine)}
        <p class="rtl-note">
          {#if backwards}<span class="rtl-reversed">Reversed: A is later than B, so the diff runs backwards in time.</span>{/if}
          {#if span && !span.oneLine}<span>A and B are on different lanes: the diff compares their files directly.</span>{/if}
        </p>
      {/if}
      {#if newCount || hasReplaced}
        <p class="rtl-legend">
          {#if newCount}<span><i class="rtl-key new" aria-hidden="true"></i>{newCount} new since your last review</span>{/if}
          {#if hasReplaced}<span><i class="rtl-key replaced" aria-hidden="true"></i>replaced by a force-push</span>{/if}
        </p>
      {/if}
    </div>
  {/if}
</section>

{#if tip && tipDetails && !menu}
  <div id="rtl-tip" class="rtl-tip" role="tooltip" style="left:{tip.left}px;top:{tip.top}px">
    <code>{tipDetails.sha}</code>
    <b>{tipDetails.title}</b>
    {#if tipDetails.meta}<span>{tipDetails.meta}</span>{/if}
    {#if points[tip.i]?.isNew}<span class="rtl-tip-note">new since your last review</span>{/if}
    {#if tip.i === timeline.reviewed}<span class="rtl-tip-note">your last review</span>{/if}
    <span class="rtl-tip-note">Click to set as From or To</span>
  </div>
{/if}
